/**
 * Returns the entity-type noun for a territory optimization view based on whether
 * its strategy targets providers (HCPs) or sites (facilities).
 *
 * This is the single source of truth for the "Provider(s)" / "Site(s)" label so
 * every surface — the chat system message, the results/comparison territory info
 * cards, and the metrics strips — stays consistent. Use this rather than
 * hand-rolling `isProviderFocused ? 'Providers' : 'Sites'` ternaries.
 *
 * @param isProviderFocused - true for provider/HCP-focused strategies, false for
 *   site-focused, or `null`/`undefined` when it can't be determined. Derive it
 *   from `getIsProviderFocused` (or `useJobIsProviderFocused`).
 * @param form - 'singular' ("Provider"/"Site") or 'plural' ("Providers"/"Sites").
 *
 * Failsafe: when focus is unknown (`null`/`undefined`) we return the generic
 * "Entity"/"Entities" rather than guessing a type, so a surface never mislabels
 * the entity count as Sites or Providers when we genuinely don't know.
 */
const getEntityTypeLabel = (
  isProviderFocused: boolean | null | undefined,
  form: 'singular' | 'plural'
): string => {
  if (isProviderFocused == null) {
    return form === 'singular' ? 'Entity' : 'Entities';
  }
  if (form === 'singular') {
    return isProviderFocused ? 'Provider' : 'Site';
  }
  return isProviderFocused ? 'Providers' : 'Sites';
};

export default getEntityTypeLabel;
